/** Measure the actual fonts and content before PDF conversion. Each output
 * sheet is a physical A4 page, so numbering does not depend on converter
 * support for CSS margin boxes. No company text is shortened. */
export async function paginateSellerReport(html: string): Promise<string> {
  const frame=document.createElement('iframe');
  frame.style.cssText='position:fixed;left:-10000px;top:0;width:900px;height:1200px;visibility:hidden;pointer-events:none';
  frame.setAttribute('sandbox','allow-same-origin');
  const loaded=new Promise<void>((resolve,reject)=>{
    frame.onload=()=>resolve(); frame.onerror=()=>reject(new Error('Could not measure the report pages.'));
  });
  frame.srcdoc=html; document.body.appendChild(frame);
  try {
    await loaded;
    const doc=frame.contentDocument!;
    await doc.fonts.ready;
    await Promise.all(Array.from(doc.images).map(img=>img.complete ? Promise.resolve() : new Promise<void>(resolve=>{img.onload=()=>resolve();img.onerror=()=>resolve();})));
    const style=doc.createElement('style');
    style.textContent=`
      @page{size:A4 portrait;margin:0}
      .sheet{width:210mm!important;height:297mm!important;min-height:297mm!important;max-height:297mm!important;display:flex!important;flex-direction:column!important;padding:0 0 12mm!important;box-sizing:border-box!important;margin:0 auto 24px!important;break-after:page!important;page-break-after:always!important}
      .sheet:last-child{break-after:auto!important;page-break-after:auto!important}
      .masthead-black{padding:12mm 16mm 10mm!important;border-radius:0;flex-shrink:0}
      .mast-top{padding-bottom:16px;margin-bottom:16px}
      .tech-topbar{margin:12mm 16mm 0!important;padding:0 0 5mm!important;flex-shrink:0}
      .page-body{margin:0 16mm!important;flex:1!important;min-height:0!important;padding:6mm 0 0!important}
      .page-footer{margin:0 16mm!important;padding:4mm 0 0!important;flex-shrink:0}
      .page-number{display:block;margin:3mm 16mm 0;text-align:end;font:9px Arial,sans-serif;color:#667985;flex-shrink:0}
      .page-body>.card{margin:0 0 14px!important;padding-bottom:14px!important}
      .seller-report-facts>div{padding:7px 0!important}
      .seller-opportunity{margin-bottom:16px!important;padding:14px 16px!important}
      @media print{.sheet{margin:0!important}html,body{background:white!important}}
    `;
    doc.head.appendChild(style);
    const main=doc.querySelector('main')!;
    const chapters=Array.from(main.querySelectorAll<HTMLElement>(':scope > .sheet'));
    main.replaceChildren();
    const pages:HTMLElement[]=[];
    const clone=<T extends Node>(node:T)=>node.cloneNode(true) as T;
    let groupSerial=0;
    for(const chapter of chapters) {
      const header=chapter.querySelector<HTMLElement>(':scope > header,:scope > .tech-topbar');
      const footer=chapter.querySelector<HTMLElement>(':scope > .page-footer');
      const originalBody=chapter.querySelector<HTMLElement>(':scope > .page-body')!;
      let body:HTMLElement;
      let lastGroup='';
      let lastContainer:HTMLElement|undefined;
      const newPage=()=>{
        const page=doc.createElement('article');page.className='sheet';
        if(header) page.appendChild(clone(header));
        body=doc.createElement('div');body.className='page-body';page.appendChild(body);
        if(footer) page.appendChild(clone(footer));
        const number=doc.createElement('div');number.className='page-number';number.textContent='0 / 0';page.appendChild(number);
        main.appendChild(page);pages.push(page);lastGroup='';lastContainer=undefined;
      };
      newPage();
      const fits=()=>body.scrollHeight <= body.clientHeight+1;
      const append=(unit:HTMLElement,group='')=>{
        let parent:HTMLElement=body;
        let addition=unit;
        if(group && group===lastGroup && lastContainer) {
          const target=lastContainer.querySelector<HTMLElement>('.seller-report-facts');
          const row=unit.querySelector<HTMLElement>('.seller-report-facts>div');
          if(target&&row){parent=target;addition=clone(row);}
        }
        parent.appendChild(addition);
        if(!fits()) {
          addition.remove();
          if(body.children.length) newPage();
          body.appendChild(unit);
          if(!fits()) {
            unit.remove();
            // Oversized opportunity cards may span several pages. Repeat their
            // identity and split evidence at item boundaries; prose is split
            // without truncation only when it cannot fit on a physical page.
            if(unit.matches('.seller-opportunity') && (unit.children.length>2 || (unit.querySelector('ul')?.children.length || 0)>1)) {
              const identity=unit.querySelector('header');
              const fragments:HTMLElement[]=[];
              for(const child of Array.from(unit.children) as HTMLElement[]) {
                if(child===identity) continue;
                const evidence=child.querySelector('ul');
                if(evidence) {
                  for(const item of Array.from(evidence.children)) {
                    const part=clone(child);part.querySelector('ul')!.replaceChildren(clone(item));fragments.push(part);
                  }
                } else fragments.push(clone(child));
              }
              for(const part of fragments) {
                const fragment=doc.createElement('article');fragment.className='seller-opportunity';
                if(identity) fragment.appendChild(clone(identity));fragment.appendChild(part);
                append(fragment);
              }
              return;
            }
            const prose=unit.querySelector<HTMLElement>('dd,p,li');
            if(prose && prose.textContent && prose.textContent.length>800) {
              const value=prose.textContent;
              for(let offset=0;offset<value.length;) {
                let end=Math.min(value.length,offset+800);
                const space=value.lastIndexOf(' ',end);
                if(end<value.length && space>offset+400) end=space+1;
                const fragment=clone(unit);
                fragment.querySelector('dd,p,li')!.textContent=value.slice(offset,end);
                append(fragment);offset=end;
              }
              return;
            }
            throw new Error('This company content could not be fitted to the report page.');
          }
          lastContainer=unit;lastGroup=group;return;
        }
        if(parent===body){lastContainer=unit;lastGroup=group;}
      };
      for(const block of Array.from(originalBody.children) as HTMLElement[]) {
        const dl=block.matches('.card') ? block.querySelector<HTMLElement>(':scope > .seller-report-facts') : null;
        if(dl) {
          const group=String(++groupSerial);
          for(const row of Array.from(dl.children)) {
            const unit=clone(block), unitDl=unit.querySelector('.seller-report-facts')!;
            unitDl.replaceChildren(clone(row)); append(unit,group);
          }
        } else append(clone(block));
      }
    }
    pages.forEach((page,i)=>{page.querySelector('.page-number')!.textContent=`${i+1} / ${pages.length}`;});
    return '<!doctype html>'+doc.documentElement.outerHTML;
  } finally {frame.remove();}
}
